import { Link } from 'react-router-dom';

export interface Feature {
  icon: string;
  title: string;
  description: string;
}

export interface WhyChooseUsContent {
  headline: string;
  subheadline: string;
  body: string;
  ctaText: string;
  ctaLink: string;
  features: Feature[];
}

interface Props {
  content: WhyChooseUsContent;
}

const WhyChooseUs = ({ content }: Props) => {
  const { headline, subheadline, body, ctaText, ctaLink, features } = content;

  return (
    <section className="relative overflow-hidden bg-surface-soft">

      <div className="relative z-10 flex flex-col lg:flex-row min-h-[820px]">

        {/* ── LEFT TEXT PANEL ─────────────────────────── */}
        <div className="lg:w-[46%] flex flex-col justify-center px-10 lg:px-16 xl:px-20 py-28 relative bg-white">
          {/* Teal left accent bar */}
          <div className="absolute left-0 top-16 bottom-16 w-[3px] rounded-full bg-teal" />

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-7">
              <div className="h-px w-10 bg-teal" />
              <span className="text-teal text-[10px] tracking-[0.35em] uppercase font-sans font-semibold">
                {subheadline}
              </span>
            </div>
            <h2
              className="font-serif text-charcoal font-bold leading-[1.08] mb-7"
              style={{ fontSize: 'clamp(2.1rem, 3.6vw, 3.2rem)' }}
            >
              {headline}
            </h2>
            <div className="flex items-center gap-2.5 mb-10">
              <div className="h-[3px] w-16 bg-teal rounded-full" />
              <div className="h-[3px] w-5 rounded-full bg-teal/25" />
            </div>
            <p className="text-charcoal/75 text-[16px] leading-[2.0] font-sans mb-12 max-w-[420px]">
              {body}
            </p>
            <Link
              to={ctaLink}
              className="inline-flex items-center gap-3 bg-teal hover:bg-teal-mid text-white font-bold text-[11px] px-9 py-[15px] rounded transition-all duration-300 cursor-pointer whitespace-nowrap tracking-[0.16em] uppercase group"
            >
              {ctaText}
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>

            {/* Micro stats */}
            <div className="flex gap-10 mt-14 pt-10 border-t border-surface-border">
              {[
                { val: '500+', label: 'Five-Star Reviews' },
                { val: '10+',  label: 'Years in Service' },
                { val: '24/7', label: 'Always Available' },
              ].map(s => (
                <div key={s.label}>
                  <div className="font-serif text-charcoal font-bold text-[1.7rem] leading-none mb-2">{s.val}</div>
                  <div className="text-charcoal/60 text-[10.5px] font-sans uppercase tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── RIGHT IMAGE + FEATURE GRID ──────────────── */}
        <div className="lg:w-[54%] relative overflow-hidden" style={{ minHeight: '600px' }}>
          <img
            src="https://public.readdy.ai/ai/img_res/cbc3f70c4a4a719b49c7944cddade0ef.jpg"
            alt="Drain and Sewer Pro licensed technicians"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: 'center 30%', filter: 'brightness(0.72) contrast(1.06) saturate(0.82)' }}
          />
          {/* Light overlay from left — blends into light bg */}
          <div className="absolute inset-0"
            style={{ background: 'linear-gradient(to right, rgba(247,247,247,0.85) 0%, rgba(247,247,247,0.3) 35%, rgba(247,247,247,0.05) 68%, rgba(247,247,247,0.12) 100%)' }} />
          <div className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none"
            style={{ background: 'linear-gradient(to bottom, transparent, rgba(247,247,247,0.5))' }} />

          {/* Feature cards grid */}
          <div className="relative z-10 grid grid-cols-2 gap-4 p-10 lg:p-14 h-full content-center">
            {features.map((feat, idx) => (
              <div
                key={idx}
                className="group rounded-2xl p-6 bg-white border border-surface-border transition-all duration-400 cursor-default hover:border-teal/25 hover:-translate-y-1"
                style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.10)')}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.06)')}
              >
                <div className="w-10 h-10 flex items-center justify-center mb-4 rounded-xl transition-all duration-300 group-hover:scale-105"
                  style={{ background: 'rgba(26,74,82,0.08)', border: '1px solid rgba(26,74,82,0.14)' }}>
                  <i className={`${feat.icon} text-teal text-lg`} />
                </div>
                <h3 className="font-sans text-charcoal font-semibold text-[13.5px] mb-1.5 leading-snug group-hover:text-teal transition-colors duration-300">
                  {feat.title}
                </h3>
                <p className="text-charcoal/70 text-[12px] leading-relaxed font-sans group-hover:text-charcoal/85 transition-colors">{feat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
